import React, { useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/AddApplicant.css';

const REGISTER = `
  mutation RegisterUerByAdmin($RegisterUerByAdmin: RegisterUerByAdmin!) {
    registerUerByAdmin(RegisterUerByAdmin: $RegisterUerByAdmin) {
      id
      email
      employeeCode
      profileComplete
    }
  }
`;

function randomPassword() {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const special = '!@#$%&*';
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  return `${pick(upper)}${pick(lower)}${pick(digits)}${pick(special)}${pick(lower)}${pick(digits)}${pick(upper)}${pick(special)}`;
}

function splitName(fullName) {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

const ROLE_OPTIONS = [
  { label: 'Cleaner', value: 'FIELD_CLEANER' },
  { label: 'Manager', value: 'OPERATIONS_MANAGER' },
  { label: 'Admin', value: 'ADMIN' },
  { label: 'Contractor', value: 'SCHEDULER_DISPATCHER' },
];

/**
 * Admin-only hire form: creates the employee account with role/pay details,
 * emails portal credentials, and asks the employee to complete personal info.
 */
export default function HireEmployeeModal({
  applicant,
  onClose,
  onHired,
}) {
  const nameParts = useMemo(
    () => splitName(applicant?.name),
    [applicant?.name],
  );
  const [form, setForm] = useState({
    firstName: nameParts.firstName,
    lastName: nameParts.lastName,
    email: applicant?.email || '',
    contact: applicant?.phone || '',
    role: 'FIELD_CLEANER',
    jobTitle: applicant?.role || '',
    typeOfWork: 'Full-time',
    salaryTerm: 'Hourly',
    perHourRate: '',
    joiningDate: new Date().toISOString().slice(0, 10),
    paydayDay: 'Friday',
    paydayTime: '09:00',
    password: randomPassword(),
  });
  const [saving, setSaving] = useState(false);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error('First and last name are required.');
      return;
    }
    if (!form.email.trim()) {
      toast.error('Work email is required so the employee can log in.');
      return;
    }
    if (!form.password || form.password.length < 7) {
      toast.error('Temporary password must be at least 7 characters.');
      return;
    }

    setSaving(true);
    try {
      const username = form.email.trim().split('@')[0] || form.firstName.trim();
      const data = await graphqlRequest(REGISTER, {
        RegisterUerByAdmin: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          username,
          password: form.password,
          contact: form.contact.trim() || null,
          roles: [form.role],
          jobTitle: form.jobTitle.trim() || null,
          typeOfWork: form.typeOfWork || null,
          salaryTerm: form.salaryTerm || null,
          perHourRate: form.perHourRate
            ? parseFloat(form.perHourRate)
            : null,
          joiningDate: form.joiningDate || null,
          paydayDay: form.paydayDay || null,
          paydayTime: form.paydayTime || null,
          addedFromWorkforce: true,
          profileComplete: false,
        },
      });

      if (!data?.registerUerByAdmin?.id) {
        throw new Error('Employee account was not created.');
      }

      toast.success(
        'Employee created. Welcome email sent — they can complete personal details in the portal.',
      );
      onHired?.(data.registerUerByAdmin);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'Could not create employee account.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aa-overlay" onClick={onClose}>
      <div className="aa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aa-header">
          <div>
            <h2>Hire &amp; Send Portal Invite</h2>
            <p>
              Creates the employee portal login and emails credentials. To only
              move someone already hired, drag their card to the Hired column
              instead.
            </p>
          </div>
          <button type="button" className="aa-close" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <form className="aa-form" onSubmit={handleSubmit}>
          <div className="aa-grid">
            <label className="aa-field">
              <span>First name *</span>
              <input
                value={form.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
                required
              />
            </label>
            <label className="aa-field">
              <span>Last name *</span>
              <input
                value={form.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
                required
              />
            </label>
            <label className="aa-field">
              <span>Work email *</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                required
              />
            </label>
            <label className="aa-field">
              <span>Phone</span>
              <input
                value={form.contact}
                onChange={(e) => setField('contact', e.target.value)}
              />
            </label>
            <label className="aa-field">
              <span>Role *</span>
              <select
                value={form.role}
                onChange={(e) => setField('role', e.target.value)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="aa-field">
              <span>Job title</span>
              <input
                value={form.jobTitle}
                onChange={(e) => setField('jobTitle', e.target.value)}
              />
            </label>
            <label className="aa-field">
              <span>Employment type</span>
              <select
                value={form.typeOfWork}
                onChange={(e) => setField('typeOfWork', e.target.value)}
              >
                <option>Full-time</option>
                <option>Part-time</option>
                <option>Casual</option>
                <option>Contract</option>
              </select>
            </label>
            <label className="aa-field">
              <span>Salary term</span>
              <select
                value={form.salaryTerm}
                onChange={(e) => setField('salaryTerm', e.target.value)}
              >
                <option>Hourly</option>
                <option>Weekly</option>
                <option>Fortnightly</option>
                <option>Monthly</option>
                <option>Annual</option>
              </select>
            </label>
            <label className="aa-field">
              <span>Hourly / rate</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.perHourRate}
                onChange={(e) => setField('perHourRate', e.target.value)}
              />
            </label>
            <label className="aa-field">
              <span>Joining date</span>
              <input
                type="date"
                value={form.joiningDate}
                onChange={(e) => setField('joiningDate', e.target.value)}
              />
            </label>
            <label className="aa-field">
              <span>Payday</span>
              <input
                value={form.paydayDay}
                onChange={(e) => setField('paydayDay', e.target.value)}
              />
            </label>
            <label className="aa-field">
              <span>Pay time</span>
              <input
                type="time"
                value={form.paydayTime}
                onChange={(e) => setField('paydayTime', e.target.value)}
              />
            </label>
            <label className="aa-field aa-field--full">
              <span>Temporary password *</span>
              <input
                value={form.password}
                onChange={(e) => setField('password', e.target.value)}
                required
              />
              <small className="aa-hint">
                Sent in the welcome email. Employee should change it after login.
              </small>
            </label>
          </div>

          <div className="aa-actions">
            <button
              type="button"
              className="aa-btn aa-btn--ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="aa-btn aa-btn--primary"
              disabled={saving}
            >
              {saving ? 'Sending…' : 'Hire & Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
